import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SendIcon, MessageCircleIcon, PhoneIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
interface FormData {
  name: string;
  company: string;
  phone: string;
  email: string;
}
interface FormErrors {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
}
export function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Введіть ім'я";
    if (!formData.company.trim()) newErrors.company = 'Введіть назву компанії';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Введіть телефон';
    } else if (!/^[+\d\s()-]{7,}$/.test(formData.phone)) {
      newErrors.phone = 'Некоректний номер';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Введіть e‑mail';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Некоректний e‑mail';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  return <section className="py-20 lg:py-28 bg-white" id="contact" data-id="element-165">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-166">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-167">
          {/* Left: info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-168">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-169">
              Зв'яжіться з нами
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-170">
              Залишити заявку
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-171">
              Розкажіть про вашу задачу — ми зв'яжемося протягом 2 годин та
              запропонуємо рішення
            </p>

            <div className="mt-10 space-y-6" data-id="element-172">
              <div className="flex items-center gap-4" data-id="element-173">
                <div className="w-12 h-12 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center" data-id="element-174">
                  <PhoneIcon className="w-5 h-5" data-id="element-175" />
                </div>
                <div data-id="element-176">
                  <div className="text-sm text-navy-400 font-medium" data-id="element-177">
                    Телефон
                  </div>
                  <a href="tel:+380441234567" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-178">
                    +38 (044) 123‑45‑67
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-179">
                <div className="w-12 h-12 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center" data-id="element-180">
                  <MailIcon className="w-5 h-5" data-id="element-181" />
                </div>
                <div data-id="element-182">
                  <div className="text-sm text-navy-400 font-medium" data-id="element-183">
                    E‑mail
                  </div>
                  <a href="mailto:info@esca.ua" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-184">
                    info@esca.ua
                  </a>
                </div>
              </div>
            </div>

            {/* Messenger links */}
            <div className="mt-10" data-id="element-185">
              <p className="text-sm font-medium text-navy-500 mb-4" data-id="element-186">
                Або напишіть нам у месенджер:
              </p>
              <div className="flex gap-3" data-id="element-187">
                <a href="https://t.me/esca_agency" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#229ED9]/10 text-[#229ED9] font-semibold text-sm hover:bg-[#229ED9]/20 transition-colors" data-id="element-188">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-189" />
                  Telegram
                </a>
                <a href="https://wa.me/380441234567" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366]/10 text-[#25D366] font-semibold text-sm hover:bg-[#25D366]/20 transition-colors" data-id="element-190">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-191" />
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-192">
            {isSubmitted ? <div className="flex flex-col items-center justify-center h-full text-center p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-193">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-6" data-id="element-194">
                  <CheckCircleIcon className="w-8 h-8" data-id="element-195" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-196">
                  Заявку надіслано!
                </h3>
                <p className="text-navy-500" data-id="element-197">
                  Ми зв'яжемося з вами протягом 2 годин у робочий час
                </p>
                <button onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '',
                company: '',
                phone: '',
                email: ''
              });
            }} className="mt-6 text-primary-600 font-semibold hover:text-primary-700 transition-colors" data-id="element-198">
                  Надіслати ще одну заявку
                </button>
              </div> : <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" noValidate data-id="element-199">
                <div className="space-y-5" data-id="element-200">
                  {/* Name */}
                  <div data-id="element-201">
                    <label htmlFor="name" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-202">
                      Ім'я *
                    </label>
                    <input id="name" type="text" value={formData.name} onChange={handleChange('name')} placeholder="Ваше ім'я" className={`w-full px-4 py-3 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 ${errors.name ? 'border-red-400' : 'border-navy-200'}`} data-id="element-203" />
                    {errors.name && <p className="mt-1 text-sm text-red-500" data-id="element-204">{errors.name}</p>}
                  </div>

                  {/* Company */}
                  <div data-id="element-205">
                    <label htmlFor="company" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-206">
                      Компанія *
                    </label>
                    <input id="company" type="text" value={formData.company} onChange={handleChange('company')} placeholder="Назва компанії" className={`w-full px-4 py-3 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 ${errors.company ? 'border-red-400' : 'border-navy-200'}`} data-id="element-207" />
                    {errors.company && <p className="mt-1 text-sm text-red-500" data-id="element-208">
                        {errors.company}
                      </p>}
                  </div>

                  {/* Phone */}
                  <div data-id="element-209">
                    <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-210">
                      Телефон *
                    </label>
                    <input id="phone" type="tel" value={formData.phone} onChange={handleChange('phone')} placeholder="+38 (0XX) XXX‑XX‑XX" className={`w-full px-4 py-3 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 ${errors.phone ? 'border-red-400' : 'border-navy-200'}`} data-id="element-211" />
                    {errors.phone && <p className="mt-1 text-sm text-red-500" data-id="element-212">
                        {errors.phone}
                      </p>}
                  </div>

                  {/* Email */}
                  <div data-id="element-213">
                    <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-1.5" data-id="element-214">
                      E‑mail *
                    </label>
                    <input id="email" type="email" value={formData.email} onChange={handleChange('email')} placeholder="your@email.com" className={`w-full px-4 py-3 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 ${errors.email ? 'border-red-400' : 'border-navy-200'}`} data-id="element-215" />
                    {errors.email && <p className="mt-1 text-sm text-red-500" data-id="element-216">
                        {errors.email}
                      </p>}
                  </div>
                </div>

                <button type="submit" disabled={isSubmitting} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-217">
                  {isSubmitting ? <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-218" />
                      Надсилання...
                    </> : <>
                      <SendIcon className="w-4 h-4" data-id="element-219" />
                      Надіслати заявку
                    </>}
                </button>

                <p className="mt-4 text-xs text-navy-400 text-center" data-id="element-220">
                  Натискаючи «Надіслати», ви погоджуєтесь з політикою
                  конфіденційності
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}